import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { api, errorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';

/**
 * Franja permanente mientras dura un «ver como». Va en los dos armazones y no se puede
 * cerrar: si desapareciera, el administrador podría creerse dentro de su propia sesión y
 * no entender por qué no puede guardar nada.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const info = session?.impersonation;
  if (!info) return null;

  const minutes = Math.max(0, Math.round((new Date(info.expiresAt).getTime() - Date.now()) / 60_000));

  const stop = async () => {
    // Sin esto, un doble toque lanza dos vueltas: la segunda llega con la sesión ya cerrada
    // y responde 401, que el cliente interpreta como sesión caducada.
    if (busy) return;
    setBusy(true);
    try {
      await api('/auth/impersonation/stop', { method: 'POST', body: {} });
      qc.clear();
      // Recarga completa: la caché de consultas y el CSRF son de la otra identidad.
      window.location.replace('/admin');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 flex flex-wrap items-center gap-3 border-b-2 border-amber-500 bg-amber-100 px-4 py-2 text-amber-950"
    >
      <Eye className="size-5 shrink-0" aria-hidden />
      <p className="text-sm font-medium">
        {t('impersonation.viewingAs', { name: session?.user.fullName, user: session?.user.username })}
        {session?.terminal ? ` · ${session.terminal.code} · ${session.terminal.name}` : ''}
        {' · '}
        <span className="font-normal">{t('impersonation.readOnly')}</span>
        {minutes > 0 ? <span className="font-normal"> · {t('impersonation.minutesLeft', { count: minutes })}</span> : null}
      </p>
      <Button size="sm" className="ml-auto" disabled={busy} loading={busy} onClick={() => void stop()}>
        <Undo2 /> {t('impersonation.back')}
      </Button>
    </div>
  );
}
